import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache, QueryCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import * as Sentry from "@sentry/react";
import { captureError } from "@/lib/logger";

// Pages
import Auth from "./pages/Auth";
import DashboardLayout from "./components/layout/DashboardLayout";
import Portfolio from "./pages/dashboard/Portfolio";
import Analytics from "./pages/dashboard/Analytics";
import Transactions from "./pages/dashboard/Transactions";
import VehiclesAnalyticsPage from "./pages/dashboard/VehiclesAnalyticsPage";
import VehiclesRecordsPage from "./pages/dashboard/VehiclesRecordsPage";
import VehiclesProcurementPage from "./pages/dashboard/VehiclesProcurementPage";
import VehiclesContractsPage from "./pages/dashboard/VehiclesContractsPage";
import VehiclesTransactionsPage from "./pages/dashboard/VehiclesTransactionsPage";
import Drivers from "./pages/dashboard/Drivers";
import DriverApplications from "./pages/dashboard/DriverApplications";
import GPSTrackers from "./pages/dashboard/GPSTrackers";
import Contracts from "./pages/dashboard/Contracts";
import ContractAgreements from "./pages/dashboard/ContractAgreements";
import ContractDeposits from "./pages/dashboard/ContractDeposits";
import ContractInstalments from "./pages/dashboard/ContractInstalments";
import ContractWitnesses from "./pages/dashboard/ContractWitnesses";
import ContractRepayments from "./pages/dashboard/ContractRepayments";
import ContractInsights from "./pages/dashboard/ContractInsights";
import ContractSetup from "./pages/dashboard/ContractSetup";
import AdvanceSetup from "./pages/dashboard/AdvanceSetup";
import InsuranceSetup from "./pages/dashboard/InsuranceSetup";
import AgreementTemplates from "./pages/dashboard/AgreementTemplates";
import Settings from "./pages/dashboard/Settings";
import { PermissionGuard } from "./components/PermissionGuard";
import Expenses from "./pages/dashboard/Expenses";
import Loans from "./pages/dashboard/Loans";
import LoanAgreements from "./pages/dashboard/LoanAgreements";
import LoanRepayments from "./pages/dashboard/LoanRepayments";
import LoanInstalments from "./pages/dashboard/LoanInstalments";
import Insurance from "./pages/dashboard/Insurance";
import InsurancePremiums from "./pages/dashboard/InsurancePremiums";
import InsuranceClaims from "./pages/dashboard/InsuranceClaims";
import InsuranceLoans from "./pages/dashboard/InsuranceLoans";
import InsuranceDiscounts from "./pages/dashboard/InsuranceDiscounts";
import InsuranceDeposits from "./pages/dashboard/InsuranceDeposits";
import Roadworthy from "./pages/dashboard/Roadworthy";
import Maintenance from "./pages/dashboard/Maintenance";
import Inspections from "./pages/dashboard/Inspections";
import InspectionContract from "./pages/dashboard/InspectionContract";
import InspectionCapture from "./pages/dashboard/InspectionCapture";
import Partners from "./pages/dashboard/Partners";
import PartnerDetail from "./pages/dashboard/PartnerDetail";
import PartnerApplications from "./pages/dashboard/PartnerApplications";
import PartnerPayoutAssignments from "./pages/dashboard/PartnerPayoutAssignments";
import PartnerPayoutWeek from "./pages/dashboard/PartnerPayoutWeek";
import PartnerEarningsWeek from "./pages/dashboard/PartnerEarningsWeek";
import VehicleDetail from "./pages/dashboard/VehicleDetail";
import Valuations from "./pages/dashboard/Valuations";
import PricingPage from "./pages/dashboard/PricingPage";
import RenewalPricingPage from "./pages/dashboard/RenewalPricingPage";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => captureError(error),
  }),
  mutationCache: new MutationCache({
    onError: (error) => captureError(error),
  }),
});

const App = () => (
  <Sentry.ErrorBoundary fallback={<p>Something went wrong. Our team has been notified.</p>} onError={(error) => captureError(error)}>
    <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/dashboard" element={<DashboardLayout />}>
              {/* Overview */}
              <Route index element={<Analytics />} />
              <Route path="portfolio" element={<Portfolio />} />
              <Route path="transactions" element={<Transactions />} />

              {/* Fleet */}
              <Route path="vehicles" element={<VehiclesAnalyticsPage />} />
              <Route path="vehicles/records" element={<VehiclesRecordsPage />} />
              <Route path="vehicles/procurement" element={<VehiclesProcurementPage />} />
              <Route path="vehicles/contracts" element={<VehiclesContractsPage />} />
              <Route path="vehicles/transactions" element={<VehiclesTransactionsPage />} />
              <Route path="vehicles/:vehicleId" element={<VehicleDetail />} />
              <Route path="drivers" element={<Drivers />} />
              <Route path="driver-applications" element={<DriverApplications />} />
              <Route path="trackers" element={<GPSTrackers />} />

              {/* Business - Contracts */}
              <Route path="contracts" element={<Contracts />} />
              <Route path="contracts/:contractId" element={<ContractInsights />} />
              {/* Setup lives under /dashboard/contracts, so canAccessRoute's first-segment
                  rule already gates it with the rest of the contracts area.

                  ONE MOUNT, SIX AREAS. The optional `:area` segment is what makes each area a
                  real route (back button, bookmark, deep link) without remounting the page
                  between them — a second <Route> would tear down every open draft on the way in
                  and out. The bare path is the landing case: it resolves to the area that needs
                  the admin and replaces itself. */}
              <Route path="contracts/:contractId/setup/:area?" element={<ContractSetup />} />
              {/* The advance dossier — the same shape over one `loans` row, and under the same
                  first path segment, so `canAccessRoute` gates it with the rest of contracts.

                  ONE MOUNT, TWO MODES, THREE AREAS. `:loanId` is a number or the literal `new`,
                  which is what lets the create flow and every existing advance be one page rather
                  than a sheet and a page that drift apart; nothing parses that segment with
                  `parseInt`, because `parseInt('new')` is `NaN` and a `NaN` reaches PostgREST as a
                  filter on a row that cannot exist. The optional `:area` is the contract dossier's
                  own arrangement: a real route per area without remounting between them, and the
                  bare path resolves to wherever the work is and replaces itself. */}
              <Route
                path="contracts/:contractId/advances/:loanId/:area?"
                element={<AdvanceSetup />}
              />
              {/* The insurance policy dossier — the same shape again, over one
                  `insurance_contracts` junction, and under the same first path segment so
                  `canAccessRoute` gates it with the rest of contracts.

                  ONE MOUNT, TWO MODES, FOUR AREAS. `:policyId` is a number or the literal `new`,
                  which is what lets the create flow and every existing year of cover be one page
                  rather than a side sheet and a page that drift apart; nothing parses that segment
                  with `parseInt`, because `parseInt('new')` is `NaN` and a `NaN` reaches PostgREST
                  as a filter on a row that cannot exist. The optional `:area` is the contract
                  dossier's own arrangement: a real route per area without remounting between them,
                  and the bare path resolves to wherever the work is and replaces itself.

                  THE SUBJECT IS THE POLICY AND THE CONTRACT IS THE OTHER HALF OF IT. A policy may
                  be shared and a contract may carry several years [D-B17], so the junction this
                  page edits is the one row carrying BOTH ids — which is why the contract stays in
                  the path rather than the policy standing on its own. */}
              <Route
                path="contracts/:contractId/insurance/:policyId/:area?"
                element={<InsuranceSetup />}
              />
              <Route path="contracts/agreements" element={<ContractAgreements />} />
              <Route path="contracts/deposits" element={<ContractDeposits />} />
              <Route path="contracts/instalments" element={<ContractInstalments />} />
              <Route path="contracts/witnesses" element={<ContractWitnesses />} />
              <Route path="repayments" element={<ContractRepayments />} />

              {/* Agreement templates — the wording every generated document is set from.

                  TWO NAMES, ONE ROW, AND THEY ARE NOT INTERCHANGEABLE. `PermissionGuard` calls
                  `hasAccess`, which matches `admin_permission_definitions.key` — the real key is
                  `agreements.templates`, the same one the three template RPCs enforce
                  server-side, so a guard keyed on anything else lets an admin onto a page whose
                  every action the database will refuse. `canAccessRoute` (the nav and the
                  DashboardLayout redirect) matches instead on that row's `routes`, against the
                  FIRST path segment after /dashboard — `agreements`, not `contracts`. Both come
                  off the same definition row: key `agreements.templates`, routes `[agreements]`. */}
              <Route
                path="agreements/templates"
                element={
                  <PermissionGuard permission="agreements.templates">
                    <AgreementTemplates />
                  </PermissionGuard>
                }
              />

              {/* Business - Loans */}
              <Route path="loans" element={<Loans />} />
              <Route path="loans/agreements" element={<LoanAgreements />} />
              <Route path="loans/repayments" element={<LoanRepayments />} />
              <Route path="loans/instalments" element={<LoanInstalments />} />

              {/* Business - Other */}
              <Route path="expenses" element={<Expenses />} />

              {/* Business - Valuations & Pricing */}
              <Route path="valuations" element={<Valuations />} />
              <Route path="pricing" element={<PricingPage />} />
              <Route path="pricing/renewals" element={<RenewalPricingPage />} />

              {/* Compliance - Insurance */}
              <Route path="insurance" element={<Insurance />} />
              <Route path="insurance/premiums" element={<InsurancePremiums />} />
              <Route path="insurance/claims" element={<InsuranceClaims />} />
              <Route path="insurance/loans" element={<InsuranceLoans />} />
              <Route path="insurance/discounts" element={<InsuranceDiscounts />} />
              <Route path="insurance/deposits" element={<InsuranceDeposits />} />

              {/* Compliance - Vehicle */}
              <Route path="roadworthy" element={<Roadworthy />} />
              <Route path="maintenance" element={<Maintenance />} />
              <Route path="inspections" element={<Inspections />} />
              {/* Under `inspections`, so an admin holding only that key reaches them: the layout's
                  guard reads the first path segment. */}
              <Route path="inspections/contract/:contractId" element={<InspectionContract />} />
              <Route path="inspections/new/:vehicleId" element={<InspectionCapture />} />

              {/* Partners */}
              <Route path="partners" element={<Partners />} />
              <Route path="partners/:partnerId" element={<PartnerDetail />} />
              <Route path="partner-applications" element={<PartnerApplications />} />
              <Route path="partners/assignments" element={<PartnerPayoutAssignments />} />
              <Route path="partners/payouts/week" element={<PartnerPayoutWeek />} />
              <Route path="partners/earnings/week" element={<PartnerEarningsWeek />} />

              {/* Settings — the first route with its own permission key, seeded by the
                  agreement e-signing migration. */}
              <Route
                path="settings"
                element={
                  <PermissionGuard permission="settings">
                    <Settings />
                  </PermissionGuard>
                }
              />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
  </Sentry.ErrorBoundary>
);

export default App;
